import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Flame, DollarSign, Leaf, Clock, Package, TrendingDown, Heart, Recycle } from 'lucide-react';
import { Link } from 'react-router-dom';

const EducationPage = () => {
  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <div className="mb-16">
          <h1 className="text-6xl md:text-8xl font-bold tracking-tighter uppercase leading-[0.9] mb-4 font-heading">
            Why <span className="text-lime-400">Briquettes</span>?
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-zinc-300 max-w-3xl">
            Everything you need to know about premium biomass briquettes - the reliable, eco-friendly alternative to LPG.
          </p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-8 mb-12">
          <h2 className="font-heading text-3xl font-bold uppercase mb-6 text-lime-400">What Are Briquettes?</h2>
          <p className="text-zinc-300 leading-relaxed text-lg mb-4">
            <strong>90mm biomass briquettes</strong> are compressed sawdust/agro-waste pellets engineered for clean, 
            consistent cooking heat. Unlike LPG cylinders that run dry during shortages, briquettes burn steadily for 
            3-4 hours/kg with <strong>3800-4200 kcal energy</strong> - matching gas performance at <strong className="text-lime-400">₹800/25kg bag (₹32/kg)</strong>.
          </p>
          <p className="text-zinc-300 leading-relaxed text-lg">
            Perfect for rice, rotis, grilling, tandoor - <strong>zero cylinder hassle</strong>.
          </p>
        </div>

        <div className="mb-16">
          <h2 className="font-heading text-4xl font-bold uppercase mb-8">5 Core Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors">
              <DollarSign className="w-10 h-10 text-lime-400 mb-4" />
              <h3 className="font-heading text-2xl font-bold uppercase mb-3">Saves 60%+</h3>
              <p className="text-zinc-300 leading-relaxed">
                ₹375/25kg bag vs LPG ₹1100/cylinder. One bag delivers cooking power 
                equivalent to 5 LPG cylinders! For a restaurant using 20kg/day, that's just <strong>₹300/month</strong> vs 
                <strong>₹33,000 with LPG</strong>.
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors">
              <Clock className="w-10 h-10 text-orange-500 mb-4" />
              <h3 className="font-heading text-2xl font-bold uppercase mb-3">Always Available</h3>
              <p className="text-zinc-300 leading-relaxed">
                No booking, no 45-day queues, no anxiety. <strong>Same-day Bengaluru delivery</strong>. Stock up and never 
                worry about running out during peak cooking hours or festivals.
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors">
              <Heart className="w-10 h-10 text-lime-400 mb-4" />
              <h3 className="font-heading text-2xl font-bold uppercase mb-3">Healthier</h3>
              <p className="text-zinc-300 leading-relaxed">
                <strong>70% less smoke</strong> than wood/coal. No risk of CO leaks like faulty LPG regulators. Better air 
                quality for your kitchen and family.
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors">
              <Leaf className="w-10 h-10 text-lime-400 mb-4" />
              <h3 className="font-heading text-2xl font-bold uppercase mb-3">Eco-Friendly</h3>
              <p className="text-zinc-300 leading-relaxed">
                Made from sawdust and rice husk waste - <strong>zero trees cut</strong>. Pure waste-to-energy conversion. 
                Ash makes excellent natural fertilizer for plants.
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors md:col-span-2">
              <Package className="w-10 h-10 text-orange-500 mb-4" />
              <h3 className="font-heading text-2xl font-bold uppercase mb-3">Versatile</h3>
              <p className="text-zinc-300 leading-relaxed">
                Works perfectly with rice cookers, tandoors, pressure cookers, BBQ grills, angithis, and traditional stoves. 
                <strong>1kg briquettes</strong> = 1kg meat / 1.5kg rice / 30-40 rotis cooked.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-8 mb-16">
          <div className="max-w-3xl">
            <h2 className="font-heading text-4xl font-bold uppercase mb-4 text-black">
              Bengaluru LPG Crisis
            </h2>
            <p className="text-black text-lg leading-relaxed mb-4">
              Since Q1 2026, Bengaluru has faced <strong>daily LPG shortages</strong> with waitlists exceeding <strong>45 days</strong>. 
              Prices have surged <strong>28% year-over-year</strong> to ₹1100+ per cylinder.
            </p>
            <p className="text-black text-lg leading-relaxed">
              Briquettes remain <strong>abundantly available</strong> at stable ₹800/25kg with same-day delivery. 
              Thousands of Bengaluru households and restaurants have already made the switch.
            </p>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="font-heading text-4xl font-bold uppercase mb-8">How to Use - 4 Simple Steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <div className="w-14 h-14 bg-lime-400 text-black font-bold text-2xl flex items-center justify-center mb-4">
                1
              </div>
              <h3 className="font-heading text-xl font-bold uppercase mb-3">Setup</h3>
              <p className="text-zinc-300 leading-relaxed">
                Place 4-6 briquettes in stove base. Chimney or angithi-style stoves work best for optimal airflow.
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <div className="w-14 h-14 bg-lime-400 text-black font-bold text-2xl flex items-center justify-center mb-4">
                2
              </div>
              <h3 className="font-heading text-xl font-bold uppercase mb-3">Light</h3>
              <p className="text-zinc-300 leading-relaxed">
                Add newspaper or kitchen paper underneath. Takes 8-10 minutes to reach glowing embers. No kerosene needed.
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <div className="w-14 h-14 bg-lime-400 text-black font-bold text-2xl flex items-center justify-center mb-4">
                3
              </div>
              <h3 className="font-heading text-xl font-bold uppercase mb-3">Cook</h3>
              <p className="text-zinc-300 leading-relaxed">
                1kg briquettes = 2-3 hour cooking session. 1 bag (25kg) cooks 25kg of food or makes 25 meals!
              </p>
            </div>
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <div className="w-14 h-14 bg-lime-400 text-black font-bold text-2xl flex items-center justify-center mb-4">
                4
              </div>
              <h3 className="font-heading text-xl font-bold uppercase mb-3">Cleanup</h3>
              <p className="text-zinc-300 leading-relaxed">
                Ash goes straight into your garden as natural fertilizer. No sooty mess like coal or wood.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-8 mb-12">
          <h2 className="font-heading text-3xl font-bold uppercase mb-6">LPG vs Briquettes Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="py-3 px-4 font-heading uppercase text-lime-400">Factor</th>
                  <th className="py-3 px-4 font-heading uppercase text-orange-500">LPG (2026)</th>
                  <th className="py-3 px-4 font-heading uppercase text-lime-400">Briquettes</th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                <tr className="border-b border-zinc-800">
                  <td className="py-3 px-4 font-semibold">Price</td>
                  <td className="py-3 px-4">₹1100+ per cylinder</td>
                  <td className="py-3 px-4 text-lime-400">₹800 per 25kg bag</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-3 px-4 font-semibold">Availability</td>
                  <td className="py-3 px-4 text-red-400">45-day waitlist</td>
                  <td className="py-3 px-4 text-lime-400">Same-day delivery</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-3 px-4 font-semibold">Energy Output</td>
                  <td className="py-3 px-4">~11,000 kcal/kg</td>
                  <td className="py-3 px-4">3800-4200 kcal/kg</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-3 px-4 font-semibold">Environmental Impact</td>
                  <td className="py-3 px-4">Fossil fuel, non-renewable</td>
                  <td className="py-3 px-4 text-lime-400">Waste-to-energy, renewable</td>
                </tr>
                <tr className="border-b border-zinc-800">
                  <td className="py-3 px-4 font-semibold">Safety</td>
                  <td className="py-3 px-4 text-orange-500">Gas leak risk</td>
                  <td className="py-3 px-4 text-lime-400">No leak risk</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-semibold">Storage</td>
                  <td className="py-3 px-4">Requires safety clearance</td>
                  <td className="py-3 px-4 text-lime-400">Stack anywhere, no hazard</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-gradient-to-br from-lime-400 to-lime-500 p-8 text-center">
          <h2 className="font-heading text-4xl font-bold uppercase mb-4 text-black">
            Ready to Make the Switch?
          </h2>
          <p className="text-black text-lg mb-6 max-w-2xl mx-auto">
            Join thousands of Bengaluru homes and restaurants who've said goodbye to LPG queues. 
            Use code <strong className="font-mono">FIRSTBAG</strong> for your first order at ₹750!
          </p>
          <Link
            to="/products"
            className="inline-block bg-black text-lime-400 hover:bg-zinc-900 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#000000] transition-all hover:-translate-y-1"
            data-testid="education-cta-btn"
          >
            Shop Briquettes Now
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default EducationPage;
